import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, X, MapPin, Zap, ArrowRight } from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";

/**
 * CtaPopup — Pop-up d'interaction en bas à gauche.
 * Apparaît après 6 secondes, persiste jusqu'à fermeture manuelle.
 * Personnalisé avec la ville détectée pour maximiser la conversion.
 */
const STORAGE_KEY = "voltis_cta_popup_dismissed";

export default function CtaPopup() {
  const { city } = useGeo();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Si l'utilisateur l'a déjà fermé pendant cette session, on n'affiche plus
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      // ignore
    }
    const t = setTimeout(() => setVisible(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        // ignore
      }
    }, 220);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cta-popup"
      className={`fixed bottom-6 left-6 z-[60] w-[calc(100%-3rem)] sm:w-[360px] max-w-md
                  bg-white border border-slate-200 shadow-2xl shadow-slate-900/20
                  ${closing ? "popup-leave" : "popup-enter"}`}
      role="dialog"
      aria-label="Offre d'intervention"
    >
      {/* Top accent bar */}
      <div className="h-1 bg-[#EAB308]" />

      {/* Close button */}
      <button
        onClick={close}
        data-testid="cta-popup-close"
        className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-sm transition-colors"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex w-9 h-9 items-center justify-center bg-[#0F172A] text-[#EAB308]">
            <Zap className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold leading-tight">
              Voltis Électricité
            </p>
            {city && (
              <p className="text-xs text-slate-700 inline-flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 text-[#EAB308]" />
                {city}
              </p>
            )}
          </div>
        </div>

        {/* Headline */}
        <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 leading-snug">
          {city ? (
            <>
              Besoin d'un électricien à <span className="text-[#CA8A04]">{city}</span> ?
            </>
          ) : (
            <>
              Besoin d'un électricien <span className="text-[#CA8A04]">rapidement</span> ?
            </>
          )}
        </h3>

        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Intervention en <strong className="text-slate-900">30 minutes</strong>.
          Devis gratuit, garantie 2 ans.
        </p>

        {/* CTAs */}
        <div className="mt-5 grid gap-2.5">
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="cta-popup-call"
            onClick={close}
            className="inline-flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-4 py-3 rounded-sm transition-colors text-sm"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            Appeler maintenant — {SITE.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="cta-popup-quote"
            onClick={close}
            className="inline-flex items-center justify-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-slate-900 font-semibold px-4 py-2.5 rounded-sm transition-colors text-sm"
          >
            Demander un devis gratuit
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Trust footer */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Disponible maintenant
          </span>
          <span className="text-slate-300">•</span>
          <span>7j/7 · 24h/24</span>
        </div>
      </div>
    </div>
  );
}
